import {
  MISSING,
  keyOf,
  needsReconciliation,
  stockIntegrityIssueCounts,
  stockIntegritySummary,
} from '../inventoryView';
import type { AdjustReason, InventoryScreenRow } from '../inventoryView';
import type { StockAdjustConflict, StockAdjustPopoverProps } from './StockAdjustPopover';
import { StockAdjustPopover } from './StockAdjustPopover';
import { PencilIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { HelpTip } from '@/pages/fulfillment/components/HelpTip';

export interface NeedsFixingFirstProps {
  /** The rows whose book does not add up and that the audit is not counting, in walk order. */
  rows: InventoryScreenRow[];
  auditOn: boolean;
  /** locationCode → the round this walk holds there; a shelf carries one open round, so ✎ waits for the walk. */
  openRoundsByShelf: Record<string, string>;
  /**
   * The audit is opening, confirming or closing its rounds, or the page is still reading whether one is standing,
   * so which shelves it holds is not settled yet.
   */
  auditBusy: boolean;
  fixingKey: string | null;
  savedKey: string | null;
  busy: boolean;
  onToggleFix: (row: InventoryScreenRow) => void;
  onApply: (
    row: InventoryScreenRow,
    countedQty: number,
    reason: AdjustReason | null,
    expectedOnHand: number,
    acknowledgeShortfall: boolean,
    expectedVersion: number | undefined,
  ) => void;
  onCancel: () => void;
  /** The refused save on the open box, if the shelf moved under it. */
  conflict?: StockAdjustConflict | null;
  onShowHolders: (row: InventoryScreenRow) => void;
  loadHolders: StockAdjustPopoverProps['loadHolders'];
}

const shelfOf = (row: InventoryScreenRow): string => row.locationCode || 'Unassigned';

/** What the list is and the two ways out of it, behind the (?) on its title [SSD-7472]. */
const HELP_LINES = [
  'Every row here has figures that do not add up: either more units are held for orders than the shelf holds (Reserved is bigger than Total), or Available is below zero for another reason. A stock audit skips them, because counting a shelf whose figures are already wrong would seal the wrong figure.',
  'When Reserved is bigger than Total, repair the row one of two ways. If the shelf really holds more than Total says, use ✎ next to Total and enter what is there. If an order is holding units it should not (a cancelled or refunded order that kept its hold, for example), open the Reserved number and release that hold.',
  'Once Reserved is no bigger than Total, the row leaves this list and the next audit counts it.',
  "While a stock audit has a row's shelf open, ✎ on that row stays locked. Confirm or exit the audit first.",
];
const RECONCILE_LINE =
  'A row whose Total already covers Reserved but whose Available is still negative cannot be repaired with ✎. Its stock record has to be reconciled.';

/**
 * The rows a stock audit leaves out because their book does not add up, with the figures that say so and
 * count corrections where reserved exceeds stock, or reconciliation guidance for negative availability alone [SSD-6932].
 * Naming the skus is still the point [E-62] — the list is what makes the warning actionable.
 */
export const NeedsFixingFirst = ({
  rows,
  auditOn,
  openRoundsByShelf,
  auditBusy,
  fixingKey,
  savedKey,
  busy,
  onToggleFix,
  onApply,
  onCancel,
  onShowHolders,
  loadHolders,
  conflict = null,
}: NeedsFixingFirstProps) => {
  const one = rows.length === 1;
  const { reservedExceedsStock, negativeAvailableOnly } = stockIntegrityIssueCounts(rows);
  const lockedShelves = [
    ...new Set(rows.filter((row) => openRoundsByShelf[row.locationCode]).map((row) => shelfOf(row))),
  ];
  const issue = stockIntegritySummary(rows);
  const repair =
    negativeAvailableOnly === 0
      ? `Repair the total with ✎ or release the orders under Reserved, and the ${one ? 'row joins' : 'rows join'} the next audit.`
      : `${reservedExceedsStock > 0 ? 'For rows where reserved exceeds stock, repair the total with ✎ or release the orders under Reserved. ' : ''}The existing count adjustment cannot repair negative availability when the total covers reservations. Have the stock record reconciled before the next audit.`;

  return (
    <section aria-label="Stock integrity" className="note warn fix-first">
      <div className="fix-first-head">
        <b>Needs fixing first</b>
        <HelpTip
          topic="Needs fixing first"
          lines={negativeAvailableOnly > 0 ? [...HELP_LINES, RECONCILE_LINE] : HELP_LINES}
        />
        {/* Only the sentence is announced: an alert around the table, or the help, would be read out again on every ✎. */}
        <span role="alert">
          {' '}
          — {issue}
          {auditOn
            ? `, so this audit does not count ${one ? 'it' : 'them'}. `
            : `. A stock audit leaves ${one ? 'it' : 'them'} out. `}
          {repair}
          {lockedShelves.length > 0 &&
            ` ✎ stays locked on ${lockedShelves.join(', ')} while this audit has ${
              lockedShelves.length === 1 ? 'that shelf' : 'those shelves'
            } open.`}
        </span>
      </div>
      <table className="tbl" aria-label="Needs fixing first">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Product</th>
            <th>Location</th>
            <th>Total</th>
            <th>Reserved</th>
            <th>Available</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = keyOf(row);
            const held = Boolean(openRoundsByShelf[row.locationCode]);
            const reconcileOnly = needsReconciliation(row);
            const locked = held || auditBusy || reconcileOnly;
            return (
              <tr key={key}>
                <td className="num">{row.sku}</td>
                <td className="name-col">
                  {row.brand && <b>{row.brand} </b>}
                  <span>{row.nameKor ?? row.name ?? MISSING}</span>
                </td>
                <td>
                  <span className="loc-pill">{shelfOf(row)}</span>
                </td>
                <td className="num tot-cell">
                  <span className="tot-v">{row.onHand}</span>
                  <button
                    type="button"
                    className="tot-edit"
                    aria-label={`Repair stock quantity ${row.sku} on ${shelfOf(row)}`}
                    title={
                      reconcileOnly
                        ? 'Count adjustment cannot repair this integrity issue; the stock record needs reconciliation.'
                        : held
                          ? `Shelf ${shelfOf(row)} is open in this audit`
                          : auditBusy
                            ? 'Checking whether a stock audit holds this shelf'
                            : undefined
                    }
                    disabled={locked}
                    onClick={() => onToggleFix(row)}
                  >
                    <PencilIcon />
                  </button>
                  {savedKey === key && <span className="tot-saved">✓ Saved</span>}
                  {fixingKey === key && !locked && (
                    <StockAdjustPopover
                      row={row}
                      busy={busy}
                      onApply={(countedQty, reason, expectedOnHand, acknowledgeShortfall, expectedVersion) =>
                        onApply(row, countedQty, reason, expectedOnHand, acknowledgeShortfall, expectedVersion)
                      }
                      onCancel={onCancel}
                      loadHolders={loadHolders}
                      conflict={conflict}
                    />
                  )}
                </td>
                <td className="num res-qty">
                  {row.reserved > 0 ? (
                    <button
                      type="button"
                      className="reslink"
                      aria-label={`Reserved orders ${row.sku} on ${shelfOf(row)}`}
                      onClick={() => onShowHolders(row)}
                    >
                      {row.reserved}
                    </button>
                  ) : (
                    0
                  )}
                </td>
                <td
                  className="num av-qty neg"
                  title={
                    reconcileOnly
                      ? `Available ${row.available} is negative despite ${row.onHand} on hand and ${row.reserved} reserved`
                      : `Reserved ${row.reserved} exceeds ${row.onHand} on hand`
                  }
                >
                  ⚠ {row.available}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};
